import * as React from "react";
import { cn } from "../lib/utils.ts";

export interface HeroBannerProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  title: React.ReactNode;
  synopsis?: React.ReactNode;
  backdropUrl: string;
  primaryCta: React.ReactNode;
  secondaryCta?: React.ReactNode;
  meta?: React.ReactNode;
}

/**
 * Above-the-fold hero used on /browse and series detail pages.
 * Backdrop fills, content lives in the bottom-third with a gradient overlay
 * for legibility (4.5:1 contrast against the burned-in image).
 */
export const HeroBanner = React.forwardRef<HTMLElement, HeroBannerProps>(
  ({ title, synopsis, backdropUrl, primaryCta, secondaryCta, meta, className, ...props }, ref) => {
    return (
      <section
        ref={ref}
        className={cn(
          "relative isolate overflow-hidden",
          "min-h-[60vh] md:min-h-[80vh]",
          className,
        )}
        {...props}
      >
        <img
          src={backdropUrl}
          alt=""
          loading="eager"
          decoding="async"
          fetchPriority="high"
          className="absolute inset-0 -z-10 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[var(--color-background)] via-[var(--color-background)]/60 to-transparent" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[var(--color-background)]/80 via-[var(--color-background)]/20 to-transparent" />

        <div className="flex h-full flex-col justify-end gap-4 px-4 pb-12 pt-32 md:max-w-2xl md:px-8 md:pb-20">
          {meta && (
            <div className="text-xs font-medium uppercase tracking-[0.15em] text-[var(--color-accent)]">
              {meta}
            </div>
          )}
          <h1 className="text-4xl font-bold leading-tight tracking-tight md:text-6xl">{title}</h1>
          {synopsis && (
            <p className="max-w-prose text-base text-[var(--color-foreground)]/90 md:text-lg">
              {synopsis}
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            {primaryCta}
            {secondaryCta}
          </div>
        </div>
      </section>
    );
  },
);
HeroBanner.displayName = "HeroBanner";
